import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import Header from "@/components/Header";
import Footer from "@/components/Footer";

const CATEGORIES = [
  {
    key: "design",
    name: "Design & Scannability",
    weight: "20%",
    why: "If it doesn't scan, nothing else matters. Contrast, quiet zone, minimum size and error correction are the fundamentals.",
    sources: [
      "ISO/IEC 18004:2015 — QR Code specification",
      "GS1 QR code quality guidelines",
    ],
  },
  {
    key: "placement",
    name: "Placement & Context",
    weight: "15%",
    why: "The physical surface, distance, dwell time and lighting decide whether the code is scanned at all.",
    sources: ["Field studies on scan-rate by placement surface"],
  },
  {
    key: "cta",
    name: "CTA & Value",
    weight: "20%",
    why: "The #1 published cause of low scan rates is missing or generic CTAs. A one-line benefit reliably lifts scans.",
    sources: [
      "Uniqode 2024 QR benchmarks — CTA framing impact",
      "Bitly 2024 QR benchmarks — CTA and incentive lift",
    ],
  },
  {
    key: "tracking",
    name: "Tracking & Analytics",
    weight: "15%",
    why: "Static codes are effectively invisible. Dynamic codes with UTMs are the difference between guessing and knowing.",
    sources: ["Google Analytics 4 UTM attribution documentation"],
  },
  {
    key: "mobile_ux",
    name: "Mobile UX (Landing Page)",
    weight: "20%",
    why: "Every second of load time costs scans. Dedicated mobile-first landings out-convert homepages, PDFs and app stores.",
    sources: [
      "Google web.dev — Core Web Vitals & mobile performance",
      "Chrome UX Report — mobile abandonment vs load time",
    ],
  },
  {
    key: "security",
    name: "Security & Trust",
    weight: "10%",
    why: "Non-branded destinations and non-HTTPS pages increasingly trigger phone-side warnings and 'quishing' concerns.",
    sources: [
      "FBI IC3 2023 QR phishing (quishing) advisory",
      "Google Safe Browsing developer documentation",
    ],
  },
];

export default function Methodology() {
  return (
    <div className="grain min-h-screen">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-xs uppercase tracking-[0.2em] text-[#C6F84C] font-bold">Methodology</div>
        <h1 className="mt-3 text-4xl sm:text-5xl font-black tracking-tighter leading-none">
          How we score you.
        </h1>
        <p className="mt-6 text-zinc-400 text-lg max-w-2xl">
          The audit is directional, not a certification. Below is exactly how each question rolls
          up into your 0–100 readiness score, and where the underlying best-practices come from.
        </p>

        <section className="mt-12 rounded-2xl border border-white/10 bg-[#121212] p-8">
          <h2 className="text-2xl font-bold tracking-tight">Scoring in three steps</h2>
          <ol className="mt-6 space-y-4 text-zinc-400">
            <li><span className="text-[#C6F84C] font-mono font-bold mr-2">01</span>Each question yields a 0–100 sub-score derived from the answer chosen (or the sum of selected options in a multi-select).</li>
            <li><span className="text-[#C6F84C] font-mono font-bold mr-2">02</span>Sub-scores are averaged within their category. The mobile-UX category optionally blends a lightweight URL check (HTTPS, viewport, response time) at 30% weight.</li>
            <li><span className="text-[#C6F84C] font-mono font-bold mr-2">03</span>Category scores are weighted by real-world impact on scan-to-conversion and summed into the overall readiness score.</li>
          </ol>
          <div className="mt-6 pt-6 border-t border-white/10 text-sm text-zinc-500">
            <b className="text-white">Guardrail:</b> "Not sure" answers score at the 25th percentile of that question — not zero — so users aren't penalised for honest uncertainty.
          </div>
        </section>

        <section className="mt-8 space-y-4">
          <h2 className="text-2xl font-bold tracking-tight">Category weights &amp; sources</h2>
          {CATEGORIES.map((c) => (
            <div key={c.key} className="rounded-xl border border-white/10 bg-[#121212] p-6" data-testid={`methodology-${c.key}`}>
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="font-display font-bold text-lg">{c.name}</h3>
                <div className="font-mono text-[#C6F84C] font-bold">{c.weight}</div>
              </div>
              <p className="mt-2 text-zinc-400">{c.why}</p>
              <div className="mt-4 text-xs uppercase tracking-widest text-zinc-500">Sources</div>
              <ul className="mt-2 text-sm text-zinc-400 space-y-1">
                {c.sources.map((s) => <li key={s}>· {s}</li>)}
              </ul>
            </div>
          ))}
        </section>

        <section className="mt-12 rounded-2xl border border-[#C6F84C]/40 bg-[#121212] p-8 lime-glow flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C6F84C] font-bold">Ready?</div>
            <h2 className="mt-2 text-2xl font-bold tracking-tight">Run the audit — 12 questions, ~3 minutes.</h2>
          </div>
          <Link
            to="/quiz"
            data-testid="methodology-start-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#C6F84C] hover:bg-[#B8FF3D] text-black font-bold px-6 py-3.5 transition-colors duration-200"
          >
            Start free audit <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
